import { doGraphqlRequest } from 'ducks/requests'
import { SETTINGS_INTEGRATIONS_PROVIDER_PAGE } from 'subapps/settings/types'
import selectIntegrationDataForProviderId from 'ducks/integrations/selectors/settings/selectIntegrationDataForProviderId'
import { doOpenDrawer } from 'ducks/drawers/actions'
import appGraphql from 'api//app_graphql'
import { oauthTokenSelector } from 'selectors/app'
import { BootstrapLoader } from 'util/bootstrap'
import * as types from '../types'
import splitAuthValues from '../utils/splitAuthValues'
import { doUpsertJiraServerCredentials } from './doUpsertJiraServerCredentials'

export {
  default as doFetchExternalResourcesForNewActions,
} from './doFetchExternalResourcesForNewActions'

export const removeIntegrationsByProviderMutation = `
  mutation IntegrationDeleteByProvider(
    $provider: IntegrationProvider!,
  ) {
    integrationDeleteByProvider(input: {
      provider: $provider
    }) {
      deletedIntegrationIds
    }
  }
`
export const doRemoveIntegrationsByProvider = provider => dispatch => {
  const variables = { provider }
  dispatch(
    doGraphqlRequest(
      types.REMOVE_INTEGRATION_BY_PROVIDER,
      removeIntegrationsByProviderMutation,
      variables,
      {
        app: true,
        meta: { provider },
        transformResponse: data =>
          data.integrationDeleteByProvider.deletedIntegrationIds,
      }
    )
  )
}
export const removeIntegrationMutation = `
  mutation IntegrationDelete(
    $integrationId: ID!,
  ){
    integrationDelete(input: {
      integrationId: $integrationId
    }) {
      deletedIntegrationId
    }
  }
`

export const doRemoveIntegration = (id, options = {}) => dispatch => {
  const variables = { integrationId: id }
  dispatch(
    doGraphqlRequest(
      types.REMOVE_INTEGRATION,
      removeIntegrationMutation,
      variables,
      {
        app: true,
        meta: { integrationId: id },
        transformResponse: data => data.deleteIntegration,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: ['integration'],
              phases: ['SUCCESS'],
            },
          ],
        },
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: true,
              content: 'Integration deleted',
            },
            success: {
              enabled: false,
            },
            failed: {
              content: 'Integration deletion failed',
              onClickAction: () => {
                dispatch(doRemoveIntegration(id, options))
              },
            },
          },
          entities: {
            targetOperation: 'remove',
            additionalActions: [
              {
                entityType: 'integration',
                entityId: id,
                stores: ['pending', 'current'],
                operation: 'remove',
                phases: ['STARTED'],
              },
            ],
          },
        },
        ...options,
      }
    )
  )
}

const fetchIntegrationsMutation = `
  query IntegrationsQuery {
    integrations {
      nodes {
        __typename
        ... on Integration {
          id
          legacyId
          provider
          settings {
            settings
          }
        }

        ... on UserIntegration {
          agent {
            id
          }
        }

        ... on ChannelIntegration {
          channel {
            id
          }
        }

        ... on ShopifyV2Integration {
          storeName
          storeDomain
        }

        ... on SalesforceIntegration {
          salesforceDomain
        }

        ... on JiraServerIntegration {
          username
          url
        }

        ... on RechargeIntegration {
          storeName
          storeDomain
          fullStoreDomain
        }
      }
    }
  }
`

function createFetchIntegrationsRequestAction() {
  return { type: types.FETCH_INTEGRATIONS_STARTED }
}

function createFetchIntegrationsSuccessAction(response) {
  const {
    json: { data },
  } = response
  return { type: types.FETCH_INTEGRATIONS_SUCCESS, payload: { ...data } }
}

function createFetchIntegrationsFailAction(error) {
  return { type: types.FETCH_INTEGRATIONS_FAILED, payload: { error } }
}

// bootstrap loader version of doFetchIntegrations() below
export function createFetchIntegrationsLoader() {
  return new BootstrapLoader(
    'integrations',
    createFetchIntegrationsRequestAction,
    createFetchIntegrationsSuccessAction,
    createFetchIntegrationsFailAction,
    getState => {
      return appGraphql(
        oauthTokenSelector(getState()),
        fetchIntegrationsMutation
      )
    }
  )
}

export const doFetchIntegrations = () => dispatch => {
  return dispatch(
    doGraphqlRequest(
      types.FETCH_INTEGRATIONS,
      fetchIntegrationsMutation,
      {},
      { app: true }
    )
  )
}

export const doOpenProviderPage = provider => () => dispatch => {
  dispatch({ type: SETTINGS_INTEGRATIONS_PROVIDER_PAGE, payload: { provider } })
}

const updateIntegrationProviderSettingsMutation = `
  mutation IntegrationProviderSettingsUpdate(
    $provider: IntegrationProvider!,
    $settings: JSON!
  ) {
    integrationProviderSettingsUpdate(input: {
      provider: $provider,
      settings: $settings
    }) {
      integrations {
        nodes {
          id
          settings {
            settings
          }
        }
      }
    }
  }
`

export const doUpdateIntegrationProviderSettings = (
  provider,
  values
) => dispatch => {
  const [settingValues, authValues] = splitAuthValues(values)
  const variables = {
    provider: provider.toUpperCase(),
    settings: settingValues,
  }

  if (provider === 'jira_server')
    dispatch(doUpsertJiraServerCredentials(authValues))
  dispatch(
    doGraphqlRequest(
      types.UPDATE_INTEGRATION_SETTINGS,
      updateIntegrationProviderSettingsMutation,
      variables,
      { app: true }
    )
  )
}

export const updateIntegrationSettingsMutation = `
  mutation IntegrationSettingsUpdate(
    $integrationId: ID!,
    $settings: JSON!
  ) {
    integrationSettingsUpdate(input: {
      integrationId: $integrationId,
      settings: $settings
    }) {
      integration {
        __typename
        ... on Integration {
          id
          provider
          settings {
            settings
          }
        }

        ... on UserIntegration {
          agent {
            id
          }
        }

        ... on ChannelIntegration {
          channel {
            id
          }
        }

        ... on ShopifyV2Integration {
          storeName
          storeDomain
        }

        ... on ShopifyIntegration {
          storeName
          storeDomain
        }

        ... on SalesforceIntegration {
          salesforceDomain
        }

        ... on JiraServerIntegration {
          username
          url
          agent {
            id
          }
        }
      }
    }
  }
`

export const doUpdateIntegrationSettings = (
  integrationId,
  values
) => dispatch => {
  const variables = {
    integrationId,
    settings: values,
  }

  dispatch(
    doGraphqlRequest(
      types.UPDATE_INTEGRATION_SETTINGS,
      updateIntegrationSettingsMutation,
      variables,
      { app: true }
    )
  )
}

export const doOpenEditIntegrationDrawer = (providerId, options = {}) => (
  dispatch,
  getState
) => {
  const state = getState()
  const {
    configureActionConfig: { drawerType: configureDrawerType } = {},
  } = selectIntegrationDataForProviderId(state, providerId)

  return dispatch(
    doOpenDrawer(providerId, configureDrawerType, providerId, options)
  )
}
